import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';

import {
  createRequisition,
  fetchBudgetCycles,
  fetchBudgetPeriods,
  fetchCostCenters,
  fetchPriorities,
  fetchRequisitionDetail,
  fetchRequisitionTypes,
  fetchResourceTypes,
  fetchUoms,
  updateRequisition,
} from './forms.apis';
import RequisitionItemDialog from './RequisitionItemDialog';
import {
  IAiExtractForm,
  ILookupItem,
  IRequisitionHeaderForm,
  IRequisitionItemForm,
  IRequisitionSavePayload,
} from './forms.models';

type Props = {
  requisitionix?: string;
  navigate?: (path: string) => void;
};

type State = {
  loading: boolean;
  saving: boolean;
  error: string;
  success: string;

  requisition: IRequisitionHeaderForm;
  items: IRequisitionItemForm[];
  aiextract: IAiExtractForm | null;

  requisitionTypes: ILookupItem[];
  periods: ILookupItem[];
  cycles: ILookupItem[];
  costcenters: ILookupItem[];
  resourceTypes: ILookupItem[];
  uoms: ILookupItem[];
  priorities: ILookupItem[];

  itemDialogOpen: boolean;
  itemDialogMode: 'add' | 'edit';
  editingItemIndex: number;
  editingItem: IRequisitionItemForm;
};

export default class RequisitionEditPage extends React.Component<any, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      loading: false,
      saving: false,
      error: '',
      success: '',

      requisition: this.getEmptyHeader(),
      items: [],
      aiextract: null,

      requisitionTypes: [],
      periods: [],
      cycles: [],
      costcenters: [],
      resourceTypes: [],
      uoms: [],
      priorities: [],

      itemDialogOpen: false,
      itemDialogMode: 'add',
      editingItemIndex: -1,
      editingItem: this.getEmptyItem(1),
    };
  }

  componentDidMount(): void {
    this.loadInitialData();
  }

  private getEmptyHeader = (): IRequisitionHeaderForm => {
    return {
      recname: '',
      shortcode: '',
      description: '',
      typeix: '',
      periodix: '',
      cycleix: '',
      costcenterix: '',
      reference: '',
      purpose: '',
      requesteddate: moment().format('YYYY-MM-DD'),
      neededfrom: '',
      neededto: '',
      aiassisted: 0,
      rawinput: '',
    };
  };

  private getEmptyItem = (lineno: number): IRequisitionItemForm => {
    return {
      lineno,
      recname: '',
      shortcode: '',
      description: '',
      typeix: '',
      quantity: '',
      uomix: '',
      unitestimate: '',
      totalestimate: '',
      neededfrom: '',
      neededto: '',
      justification: '',
      expectedoutcome: '',
      technicalspec: '',
      priorityix: '',
      kpinote: '',
    };
  };

  private loadInitialData = async (): Promise<void> => {
    this.setState({
      loading: true,
      error: '',
    });

    try {
      const [
        requisitionTypes,
        periods,
        cycles,
        costcenters,
        resourceTypes,
        uoms,
        priorities,
      ] = await Promise.all([
        fetchRequisitionTypes(),
        fetchBudgetPeriods(),
        fetchBudgetCycles(),
        fetchCostCenters(),
        fetchResourceTypes(),
        fetchUoms(),
        fetchPriorities(),
      ]);

      this.setState(
        {
          requisitionTypes,
          periods,
          cycles,
          costcenters,
          resourceTypes,
          uoms,
          priorities,
        },
        async () => {
          if (this.props.requisitionix) {
            await this.loadRequisition(this.props.requisitionix!);
          } else {
            this.setState({ loading: false });
          }
        }
      );
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load requisition editor',
      });
    }
  };

  private loadRequisition = async (requisitionix: string): Promise<void> => {
    try {
      const data = await fetchRequisitionDetail(requisitionix);

      const requisition: IRequisitionHeaderForm = {
        recix: data.requisition.recix,
        recname: data.requisition.recname || '',
        shortcode: data.requisition.shortcode || '',
        description: data.requisition.description || '',
        typeix: data.requisition.type?.recix || '',
        periodix: data.requisition.period?.recix || '',
        cycleix: data.requisition.cycle?.recix || '',
        costcenterix: data.requisition.costcenter?.recix || '',
        reference: data.requisition.reference || '',
        purpose: data.requisition.purpose || '',
        requesteddate: data.requisition.requesteddate
          ? moment(data.requisition.requesteddate).format('YYYY-MM-DD')
          : moment().format('YYYY-MM-DD'),
        neededfrom: data.requisition.neededfrom
          ? moment(data.requisition.neededfrom).format('YYYY-MM-DD')
          : '',
        neededto: data.requisition.neededto
          ? moment(data.requisition.neededto).format('YYYY-MM-DD')
          : '',
        aiassisted: data.requisition.aiassisted || 0,
        rawinput: data.requisition.rawinput || '',
      };

      const items: IRequisitionItemForm[] = (data.items || []).map((item) => ({
        recix: item.recix,
        lineno: item.lineno,
        recname: item.recname || '',
        shortcode: item.shortcode || '',
        description: item.description || '',
        typeix: item.type?.recix || '',
        quantity:
          item.quantity === null || item.quantity === undefined ? '' : item.quantity,
        uomix: item.uom?.recix || '',
        unitestimate:
          item.unitestimate === null || item.unitestimate === undefined
            ? ''
            : item.unitestimate,
        totalestimate:
          item.totalestimate === null || item.totalestimate === undefined
            ? ''
            : item.totalestimate,
        neededfrom: item.neededfrom
          ? moment(item.neededfrom).format('YYYY-MM-DD')
          : '',
        neededto: item.neededto ? moment(item.neededto).format('YYYY-MM-DD') : '',
        justification: item.justification || '',
        expectedoutcome: item.expectedoutcome || '',
        technicalspec: item.technicalspec || '',
        priorityix: item.priority?.recix || '',
        kpinote: item.kpinote || '',
      }));

      const extract:IAiExtractForm|null = data.aiextracts && data.aiextracts.length > 0
        ? {
            promptix: data.aiextracts[0].prompt?.recix || '',
            rawinput: data.aiextracts[0].rawinput || '',
            structuredoutput: data.aiextracts[0].structuredoutput || {},
            confidencevalue:
              data.aiextracts[0].confidencevalue === null ||
              data.aiextracts[0].confidencevalue === undefined
                ? ''
                : data.aiextracts[0].confidencevalue,
            modelname: data.aiextracts[0].modelname || '',
          }
        : null;

      this.setState({
        requisition,
        items,
        aiextract: extract,
        loading: false,
      });
    } catch (error: any) {
      this.setState({
        loading: false,
        error: error?.message || 'Failed to load requisition detail',
      });
    }
  };

  private handleHeaderTextChange =
    (field: keyof IRequisitionHeaderForm) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        requisition: {
          ...prevState.requisition,
          [field]: value,
        },
      }));
    };

  private handleHeaderSelectChange =
    (field: keyof IRequisitionHeaderForm) =>
    (event: SelectChangeEvent<string>): void => {
      const value = event.target.value;

      this.setState((prevState) => ({
        requisition: {
          ...prevState.requisition,
          [field]: value,
        },
      }));
    };

  private computeTotalEstimate = (): number => {
    const { items } = this.state;

    return items.reduce((sum, item) => {
      const value =
        item.totalestimate === '' ? 0 : Number(item.totalestimate || 0);
      return sum + value;
    }, 0);
  };

  private openAddItemDialog = (): void => {
    this.setState((prevState) => ({
      itemDialogOpen: true,
      itemDialogMode: 'add',
      editingItemIndex: -1,
      editingItem: this.getEmptyItem(prevState.items.length + 1),
    }));
  };

  private openEditItemDialog = (index: number): void => {
    const item = this.state.items[index];

    this.setState({
      itemDialogOpen: true,
      itemDialogMode: 'edit',
      editingItemIndex: index,
      editingItem: { ...item },
    });
  };

  private closeItemDialog = (): void => {
    this.setState({
      itemDialogOpen: false,
      editingItemIndex: -1,
    });
  };

  private saveItemDialog = (value: IRequisitionItemForm): void => {
    const { itemDialogMode, editingItemIndex } = this.state;

    if (itemDialogMode === 'add') {
      this.setState((prevState) => ({
        itemDialogOpen: false,
        items: [...prevState.items, { ...value }],
      }));
      return;
    }

    if (itemDialogMode === 'edit' && editingItemIndex >= 0) {
      this.setState((prevState) => {
        const items = [...prevState.items];
        items[editingItemIndex] = { ...value };
        return {
          itemDialogOpen: false,
          items,
          editingItemIndex: -1,
        };
      });
    }
  };

  private handleDeleteItem = (index: number): void => {
    this.setState((prevState) => {
      const items = prevState.items.filter((_item, itemIndex) => itemIndex !== index)
        .map((item, itemIndex) => ({
          ...item,
          lineno: itemIndex + 1,
        }));

      return { items };
    });
  };

  private validateForm = (): string[] => {
    const { requisition, items } = this.state;
    const errors: string[] = [];

    if (!requisition.recname.trim()) errors.push('Requisition title is required.');
    if (!requisition.description.trim()) errors.push('Description is required.');
    if (!requisition.typeix) errors.push('Requisition type is required.');
    if (!requisition.periodix) errors.push('Budget period is required.');
    if (!requisition.costcenterix) errors.push('Cost center is required.');
    if (!requisition.purpose.trim()) errors.push('Purpose is required.');
    if (!requisition.requesteddate) errors.push('Requested date is required.');
    if (items.length === 0) errors.push('At least one requisition item is required.');

    items.forEach((item, index) => {
      if (!item.recname.trim()) {
        errors.push(`Item ${index + 1}: title is required.`);
      }
      if (!item.description.trim()) {
        errors.push(`Item ${index + 1}: description is required.`);
      }
      if (!item.typeix) {
        errors.push(`Item ${index + 1}: resource type is required.`);
      }
      if (!item.justification.trim()) {
        errors.push(`Item ${index + 1}: justification is required.`);
      }
      if (item.totalestimate === '' || Number(item.totalestimate) < 0) {
        errors.push(`Item ${index + 1}: total estimate is required.`);
      }
    });

    return errors;
  };

  private handleSave = async (): Promise<void> => {
    const errors = this.validateForm();

    if (errors.length > 0) {
      this.setState({
        error: errors.join(' '),
        success: '',
      });
      return;
    }

    this.setState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      const payload: IRequisitionSavePayload = {
        requisition: {
          ...this.state.requisition,
          aiassisted: this.state.requisition.aiassisted,
        },
        items: this.state.items.map((item, index) => ({
          ...item,
          lineno: index + 1,
        })),
        aiextract: this.state.aiextract,
      };

      const result = this.props.requisitionix
        ? await updateRequisition(this.props.requisitionix, payload)
        : await createRequisition(payload);

      this.setState({
        saving: false,
        success: 'Requisition saved successfully.',
      });

      const requisitionix = result?.requisitionix || this.props.requisitionix;

      if (!this.props.requisitionix && requisitionix) {
        // const path = `/forms/requisitions/${requisitionix}/edit`;

        // if (this.props.navigate) {
        //   this.props.navigate(path);
        //   return;
        // }

        // window.location.href = path;

        if (this.props.navigate) {
          const row:any = {...payload, ...result};
          this.props.navigate('edit',row);
          return;
        }
      }
    } catch (error: any) {
      this.setState({
        saving: false,
        error: error?.message || 'Failed to save requisition',
      });
    }
  };

  // private handleBack = (): void => {
  //   if (this.props.navigate) {
  //     this.props.navigate('/forms/requisitions');
  //     return;
  //   }

  //   window.location.href = '/forms/requisitions';
  // };

  private handleBack = (): void => {
    if (this.props.onClose) {
      this.props.onClose();
      return;
    }
  };

  render(): React.ReactNode {
    const {
      loading,
      saving,
      error,
      success,
      requisition,
      items,
      requisitionTypes,
      periods,
      cycles,
      costcenters,
      resourceTypes,
      uoms,
      priorities,
      itemDialogOpen,
      itemDialogMode,
      editingItem,
    } = this.state;

    const totalEstimate = this.computeTotalEstimate();

    return (
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {this.props.requisitionix ? 'Edit Requisition' : 'New Requisition'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create or update a requisition draft with header and line items.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={this.handleBack}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={this.handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
          </Stack>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Requisition Header
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={requisition.recname}
                      onChange={this.handleHeaderTextChange('recname')}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Short Code"
                      value={requisition.shortcode}
                      onChange={this.handleHeaderTextChange('shortcode')}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Reference"
                      value={requisition.reference}
                      onChange={this.handleHeaderTextChange('reference')}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Description"
                      multiline
                      minRows={2}
                      value={requisition.description}
                      onChange={this.handleHeaderTextChange('description')}
                      disabled={loading}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel id="req-type-label">Requisition Type</InputLabel>
                      <Select
                        labelId="req-type-label"
                        value={requisition.typeix}
                        label="Requisition Type"
                        onChange={this.handleHeaderSelectChange('typeix')}
                      >
                        {requisitionTypes.map((item) => (
                          <MenuItem key={item.recix} value={item.recix}>
                            {item.recname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel id="req-period-label">Budget Period</InputLabel>
                      <Select
                        labelId="req-period-label"
                        value={requisition.periodix}
                        label="Budget Period"
                        onChange={this.handleHeaderSelectChange('periodix')}
                      >
                        {periods.map((item) => (
                          <MenuItem key={item.recix} value={item.recix}>
                            {item.recname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel id="req-cycle-label">Budget Cycle</InputLabel>
                      <Select
                        labelId="req-cycle-label"
                        value={requisition.cycleix}
                        label="Budget Cycle"
                        onChange={this.handleHeaderSelectChange('cycleix')}
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {cycles.map((item) => (
                          <MenuItem key={item.recix} value={item.recix}>
                            {item.recname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth>
                      <InputLabel id="req-costcenter-label">Cost Center</InputLabel>
                      <Select
                        labelId="req-costcenter-label"
                        value={requisition.costcenterix}
                        label="Cost Center"
                        onChange={this.handleHeaderSelectChange('costcenterix')}
                      >
                        {costcenters.map((item) => (
                          <MenuItem key={item.recix} value={item.recix}>
                            {item.recname}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Requested Date"
                      type="date"
                      value={requisition.requesteddate}
                      onChange={this.handleHeaderTextChange('requesteddate')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Needed From"
                      type="date"
                      value={requisition.neededfrom}
                      onChange={this.handleHeaderTextChange('neededfrom')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Needed To"
                      type="date"
                      value={requisition.neededto}
                      onChange={this.handleHeaderTextChange('neededto')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Purpose"
                      multiline
                      minRows={2}
                      value={requisition.purpose}
                      onChange={this.handleHeaderTextChange('purpose')}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                  sx={{ mb: 2 }}
                >
                  <Typography variant="h6">Requisition Items</Typography>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={this.openAddItemDialog}
                  >
                    Add Item
                  </Button>
                </Stack>

                {items.length === 0 ? (
                  <Alert severity="info">No items added yet.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {items.map((item, index) => (
                      <Card key={`${item.recix || 'new'}-${index}`} variant="outlined">
                        <CardContent>
                          <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" fontWeight={700}>
                                {item.lineno}. {item.recname || 'Untitled Item'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {item.description || 'No description'}
                              </Typography>

                              <Grid container spacing={1}>
                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Quantity
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.quantity === '' ? '-' : item.quantity}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Unit Estimate
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.unitestimate === '' ? '-' : item.unitestimate}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Total Estimate
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.totalestimate === '' ? '-' : item.totalestimate}
                                  </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 3 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Justification
                                  </Typography>
                                  <Typography variant="body2">
                                    {item.justification || '-'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>

                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<EditIcon />}
                                onClick={() => this.openEditItemDialog(index)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => this.handleDeleteItem(index)}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>

                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Items
                    </Typography>
                    <Typography variant="body1">{items.length}</Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Computed Total Estimate
                    </Typography>
                    <Typography variant="h6">
                      {Number(totalEstimate || 0).toLocaleString()}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      AI Assisted
                    </Typography>
                    <Typography variant="body1">
                      {requisition.aiassisted === 1 ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Input
                </Typography>

                <TextField
                  fullWidth
                  label="Raw Input"
                  multiline
                  minRows={8}
                  value={requisition.rawinput}
                  onChange={this.handleHeaderTextChange('rawinput')}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <RequisitionItemDialog
          open={itemDialogOpen}
          title={itemDialogMode === 'add' ? 'Add Requisition Item' : 'Edit Requisition Item'}
          value={editingItem}
          resourceTypes={resourceTypes}
          uoms={uoms}
          priorities={priorities}
          onClose={this.closeItemDialog}
          onSave={this.saveItemDialog}
        />
      </Box>
    );
  }
}